/**
 * Read-only ID↔BV interaction detail for the Zumale admin. Shows EVERYTHING about the
 * interaction — commercials (design fees, material budget, requirements), payments,
 * status timeline, design submissions/iterations with design images, and the BOQ —
 * all COMPLETELY READ-ONLY (no feedback/approve/payment controls, unlike BV RequestDetails).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { AlertCircle, Clock, CheckCircle2, MessageSquare, Wallet, ImageIcon, ListChecks, Palette } from "lucide-react";
import axiosInstance from "../../services/api/axios";
import PageHeader from "../../components/PageHeader";
import { getLeadDesignSubmissions, getLeadPayments } from "../../services/zumaleAdminInterior";
import { getPresignedDownloadUrls } from "../../utils/fileUtils";
import { normalizeMediaBySection } from "../../utils/designSubmissionMedia";
import BoqReadOnlyView from "../BOQ/BoqReadOnlyView";
import { humanizeStatus } from "./InteriorExecutions";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const STATUS_PILL = (s) => {
  const u = String(s || "").toUpperCase();
  if (u.includes("APPROVED")) return "bg-emerald-50 text-emerald-700";
  if (u.includes("REJECTED") || u === "LOST" || u.includes("EXPIRED")) return "bg-rose-50 text-rose-600";
  if (u.includes("PENDING")) return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
};
const PAYMENT_LABEL = { DESIGN_ADVANCE: "Design advance", DESIGN_FEE: "Design fee", EXECUTION_STAGE: "Execution installment" };

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
    <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><Icon size={16} /> {title}</h3>
    {children}
  </div>
);

const resolveItemUrl = (item, urlMap) => {
  if (typeof item === "string") return urlMap[item] || (item.startsWith("http") ? item : null);
  const direct = item?.url || item?.downloadUrl;
  if (direct) return direct;
  const key = item?.s3_key || item?.s3Key || item?.key || item?.media_key || item?.mediaKey || item?.file_key;
  return key ? urlMap[key] || null : null;
};

const InteractionDetailReadOnly = () => {
  const { leadId } = useParams();
  const location = useLocation();
  const row = location.state?.row || null;
  const lid = parseInt(leadId, 10);

  const [submissions, setSubmissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [imgUrls, setImgUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    Promise.all([
      getLeadDesignSubmissions(lid).catch(() => []),
      getLeadPayments(lid).catch(() => []),
    ])
      .then(([subs, pays]) => {
        if (!mounted) return;
        setSubmissions(Array.isArray(subs) ? subs : subs?.items ?? []);
        setPayments(Array.isArray(pays) ? pays : pays?.items ?? []);
      })
      .catch((e) => mounted && setError(e?.response?.data?.detail || "Failed to load interaction"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [lid]);

  // Presign design-iteration images.
  useEffect(() => {
    const keys = [];
    (submissions || []).forEach((sw) => (sw.designIterations || []).forEach((it) => {
      if (!it?.media) return;
      Object.values(normalizeMediaBySection(it.media) || {}).flat().forEach((item) => {
        const k = typeof item === "string" ? item : (item?.s3_key || item?.s3Key || item?.key || item?.media_key || item?.mediaKey || item?.file_key);
        if (k && typeof k === "string" && !k.startsWith("data:") && !(item && (item.url || item.downloadUrl))) keys.push(k);
      });
    }));
    const uniq = [...new Set(keys)];
    if (!uniq.length) { setImgUrls({}); return; }
    let mounted = true;
    getPresignedDownloadUrls(uniq, axiosInstance).then((res) => {
      if (!mounted) return;
      const map = {};
      (res || []).forEach((p, i) => { const u = p.download_url || p.downloadUrl || p.url; if (u) map[uniq[i]] = u; });
      setImgUrls(map);
    }).catch(() => mounted && setImgUrls({}));
    return () => { mounted = false; };
  }, [submissions]);

  const iterations = useMemo(() => {
    const all = [];
    (submissions || []).forEach((sw) => {
      const subm = sw.designSubmission || sw;
      const submissionId = subm?.designSubmissionId || subm?.design_submission_id || subm?.id;
      (sw.designIterations || []).forEach((it) => all.push({ ...it, _submissionId: submissionId }));
    });
    return all.sort((a, b) => (b.iterationNumber || 0) - (a.iterationNumber || 0));
  }, [submissions]);

  const latestIteration = iterations[0] || null;

  const timeline = useMemo(() => (row?.statusHistory || []).map((entry) => {
    const [k, v] = Object.entries(entry || {})[0] || [];
    return { status: k, at: v };
  }), [row]);

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Palette}
        title={`${row?.designerName || "Designer"} → ${row?.buyerName || "Buyer"}`}
        subtitle={[row?.unitNo, row?.projectName, row?.phaseName].filter(Boolean).join(" · ") || `Lead #${leadId}`}
        backTo="/zumale-admin/interior-executions"
        backLabel="Back to Interior Executions"
        actions={
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">Read-only</span>
            {row?.latestStatus && (
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_PILL(row.latestStatus)}`}>{humanizeStatus(row.latestStatus)}</span>
            )}
          </div>
        }
      />

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2"><AlertCircle size={16} /> {error}</div>
        )}

        {/* commercials */}
        {row && (
          <Section icon={Wallet} title="Commercials">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Design fees offered</p>
                <p className="text-base font-bold text-slate-900">{fmtINR(row.designFeesOffered)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Material budget</p>
                <p className="text-base font-bold text-slate-900">
                  {fmtINR(row.materialBudget)}
                  {row.budgetBufferPercent ? <span className="text-xs font-normal text-slate-400"> +{row.budgetBufferPercent}% buffer</span> : null}
                </p>
              </div>
            </div>
            {row.requirements && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Requirements</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{row.requirements}</p>
              </div>
            )}
          </Section>
        )}

        {/* payments */}
        <Section icon={Wallet} title="Payments">
          {loading ? (
            <p className="text-sm text-slate-400 py-3">Loading…</p>
          ) : payments.length === 0 ? (
            <p className="text-sm text-slate-400">No payments recorded.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left">
                  <th className="py-1.5">Type</th><th className="py-1.5 text-right">Amount</th>
                  <th className="py-1.5">Date</th><th className="py-1.5">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((p, i) => {
                  const type = p.paymentType || p.payment_type;
                  return (
                    <tr key={p.idLeadPaymentId || p.id || i} className="text-slate-700">
                      <td className="py-2">{PAYMENT_LABEL[type] || humanizeStatus(type)}</td>
                      <td className="py-2 text-right tabular-nums font-medium">{fmtINR(p.paymentAmount ?? p.payment_amount)}</td>
                      <td className="py-2 text-slate-500">{fmtDate(p.paymentDate ?? p.payment_date)}</td>
                      <td className="py-2 text-slate-500">{p.paymentReference ?? p.payment_reference ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>

        {/* status timeline */}
        {timeline.length > 0 && (
          <Section icon={Clock} title="Status timeline">
            <ol className="space-y-2">
              {timeline.map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_PILL(t.status)}`}>{humanizeStatus(t.status)}</span>
                  <span className="text-xs text-slate-400">{fmtDateTime(t.at)}</span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {/* iterations + design images */}
        <Section icon={MessageSquare} title="Design submissions & iterations">
          {loading ? (
            <p className="text-sm text-slate-400 py-3">Loading…</p>
          ) : iterations.length === 0 ? (
            <p className="text-sm text-slate-400">No design submissions yet.</p>
          ) : (
            <div className="space-y-3">
              {iterations.map((it) => {
                const dev = it.developerApprovalStatus ?? it.developer_approval_status;
                const buyerFeedback = it.buyerFeedback ?? it.buyer_feedback;
                const sections = it.media ? normalizeMediaBySection(it.media) : {};
                const imgs = Object.entries(sections).flatMap(([section, items]) =>
                  (items || []).map((item) => ({ section, url: resolveItemUrl(item, imgUrls) })).filter((x) => x.url));
                return (
                  <div key={it.designIterationId || it.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">Iteration #{it.iterationNumber || 1}</span>
                      {it.isFinal && <span className="text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded px-1.5 py-0.5">Final</span>}
                      {it.iterationStatus && <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_PILL(it.iterationStatus)}`}>{humanizeStatus(it.iterationStatus)}</span>}
                      {dev && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                          <CheckCircle2 size={11} className={String(dev).toUpperCase().includes("APPROVED") ? "text-emerald-500" : "text-slate-300"} />
                          Dev: {humanizeStatus(dev)}
                        </span>
                      )}
                      <span className="ml-auto text-[11px] text-slate-400">{fmtDateTime(it.createdDate || it.created_date)}</span>
                    </div>
                    {imgs.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {imgs.map((im, i) => (
                          <button key={i} onClick={() => window.open(im.url, "_blank", "noopener")} title={im.section}>
                            <img src={im.url} alt={im.section} loading="lazy"
                              style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 10, background: "#e2e8f0" }} />
                          </button>
                        ))}
                      </div>
                    )}
                    {imgs.length === 0 && (
                      <p className="text-[11px] text-slate-400 mt-2 inline-flex items-center gap-1"><ImageIcon size={11} /> No design images</p>
                    )}
                    {buyerFeedback && String(buyerFeedback).trim() && (
                      <p className="text-xs text-slate-600 mt-2 bg-slate-50 rounded-lg px-3 py-2">
                        <span className="font-semibold text-slate-500">Buyer feedback: </span>{buyerFeedback}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* BOQ (latest iteration, if any) */}
        {latestIteration && (
          <Section icon={ListChecks} title="Bill of Quantities">
            <BoqReadOnlyView
              designIterationId={latestIteration.designIterationId || latestIteration.design_iteration_id || latestIteration.id}
              designSubmissionId={latestIteration._submissionId}
            />
          </Section>
        )}
    </div>
  );
};

export default InteractionDetailReadOnly;
